import { useState, useRef, useCallback } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { Search, Loader2, Download, Check, Users, Lock, Globe, X, Import } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useImportFbGroups } from '@/api/hooks/useExtension'

interface SearchGroup {
  groupId: string
  name: string
  url: string
  members: number
  privacy: 'public' | 'private' | 'unknown'
  photo: string
  postsPerDay: number
  postsPerMonth: number
}

const fmtNum = new Intl.NumberFormat('pt-BR')

function formatMembers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function ExtractorTab({ selectedProfileIds }: { selectedProfileIds: string[] }) {
  const { t } = useLanguage()
  const [keyword, setKeyword] = useState('')
  const [limit, setLimit] = useState(50)
  const [delayMs, setDelayMs] = useState(2000)
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<SearchGroup[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [searchDone, setSearchDone] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const importGroups = useImportFbGroups()

  const handleSearch = useCallback(() => {
    if (!keyword.trim()) {
      toast.error(t('Digite uma palavra-chave'))
      return
    }

    // Reset
    setResults([])
    setSelected(new Set())
    setSearchDone(false)
    setIsSearching(true)

    // Close previous connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const token = localStorage.getItem('auth_token') || ''
    const params = new URLSearchParams({
      keyword: keyword.trim(),
      limit: String(limit),
      delay: String(delayMs),
      ...(selectedProfileIds.length === 1 ? { profile_id: selectedProfileIds[0] } : {}),
    })

    // Use fetch with streaming instead of EventSource (need auth header)
    const controller = new AbortController()

    fetch(`/api/v2/fb-extension/group-search?${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        keyword: keyword.trim(),
        limit,
        delay: delayMs,
        ...(selectedProfileIds.length === 1 ? { profile_id: selectedProfileIds[0] } : {}),
      }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({ error: 'Erro na busca' }))
          toast.error(data.error || `HTTP ${response.status}`)
          setIsSearching(false)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          setIsSearching(false)
          return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          let eventType = ''
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim()
            } else if (line.startsWith('data: ') && eventType) {
              try {
                const data = JSON.parse(line.slice(6))
                if (eventType === 'group') {
                  setResults((prev) => [...prev, data as SearchGroup])
                } else if (eventType === 'error') {
                  toast.error(data.error || 'Erro na busca')
                } else if (eventType === 'done') {
                  setSearchDone(true)
                }
              } catch { /* ignore */ }
              eventType = ''
            }
          }
        }

        setIsSearching(false)
        setSearchDone(true)
      })
      .catch((e) => {
        if (e.name !== 'AbortError') {
          toast.error(t('Erro na busca'))
        }
        setIsSearching(false)
      })

    // Store controller for cancellation
    eventSourceRef.current = { close: () => controller.abort() } as any
  }, [keyword, limit, delayMs, selectedProfileIds, t])

  const handleStop = () => {
    eventSourceRef.current?.close()
    setIsSearching(false)
    setSearchDone(true)
  }

  const toggleSelect = (groupId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === results.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(results.map((g) => g.groupId)))
    }
  }

  const handleImport = () => {
    const toImport = results.filter((g) => selected.has(g.groupId))
    if (toImport.length === 0) {
      toast.error(t('Selecione pelo menos um grupo'))
      return
    }

    const groups = toImport.map((g) => ({
      fb_group_id: g.groupId,
      name: g.name,
      url: g.url,
    }))

    importGroups.mutate(
      { groups, collection: `busca:${keyword.trim().toLowerCase()}` },
      {
        onSuccess: (data: any) => {
          toast.success(`${data.imported || groups.length} ${t('grupos importados!')}`)
          setSelected(new Set())
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const handleExportCSV = () => {
    const rows = results.filter((g) => selected.size === 0 || selected.has(g.groupId))
    if (rows.length === 0) return
    const header = 'Nome,URL,Membros,Privacidade,Posts/dia,Posts/mes'
    const csv = [
      header,
      ...rows.map(
        (g) =>
          `"${g.name.replace(/"/g, '""')}","${g.url}",${g.members},${g.privacy},${g.postsPerDay},${g.postsPerMonth}`,
      ),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `grupos-${keyword.trim()}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`${rows.length} ${t('grupos exportados')}`)
  }

  return (
    <div className="space-y-4">
      {/* Search Form */}
      <Card className="p-4">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-xs">{t('Palavra-chave')}</Label>
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t('Ex: emagrecimento, dropshipping, receitas...')}
              className="mt-1"
              onKeyDown={(e) => { if (e.key === 'Enter' && !isSearching) handleSearch() }}
            />
          </div>
          <div>
            <Label className="text-xs">{t('Limite')}</Label>
            <Input
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="mt-1 w-20"
            />
          </div>
          <div>
            <Label className="text-xs">{t('Delay (ms)')}</Label>
            <Input
              type="number"
              min={500}
              max={10000}
              step={500}
              value={delayMs}
              onChange={(e) => setDelayMs(Number(e.target.value))}
              className="mt-1 w-24"
            />
          </div>
          <div>
            {isSearching ? (
              <Button variant="destructive" size="sm" onClick={handleStop} className="gap-1.5">
                <X className="h-3.5 w-3.5" /> {t('Parar')}
              </Button>
            ) : (
              <Button size="sm" onClick={handleSearch} className="gap-1.5">
                <Search className="h-3.5 w-3.5" /> {t('Buscar')}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Results */}
      {(results.length > 0 || isSearching) && (
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isSearching && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('Buscando...')} {results.length}/{limit}
                </div>
              )}
              {searchDone && !isSearching && (
                <span className="text-sm text-muted-foreground">
                  {results.length} {t('grupos encontrados')}
                </span>
              )}
              {results.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-primary hover:underline"
                >
                  {selected.size === results.length ? t('Desmarcar todos') : t('Selecionar todos')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {selected.size > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportCSV}
                  className="gap-1.5 text-xs"
                >
                  <Download className="h-3 w-3" /> CSV ({selected.size})
                </Button>
              )}
              {results.length > 0 && selected.size === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportCSV}
                  className="gap-1.5 text-xs"
                >
                  <Download className="h-3 w-3" /> CSV ({results.length})
                </Button>
              )}
              {selected.size > 0 && (
                <Button
                  size="sm"
                  onClick={handleImport}
                  disabled={importGroups.isPending}
                  className="gap-1.5 text-xs"
                >
                  <Import className="h-3 w-3" />
                  {importGroups.isPending
                    ? t('Importando...')
                    : `${t('Importar')} ${selected.size} ${t('grupos')}`}
                </Button>
              )}
            </div>
          </div>

          {/* Group List */}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {results.map((group) => (
              <div
                key={group.groupId}
                onClick={() => toggleSelect(group.groupId)}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  selected.has(group.groupId)
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border hover:border-border/80 hover:bg-muted/30',
                )}
              >
                <Checkbox
                  checked={selected.has(group.groupId)}
                  onCheckedChange={() => toggleSelect(group.groupId)}
                  className="shrink-0"
                />

                {/* Group Photo */}
                {group.photo ? (
                  <img
                    src={group.photo}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-lg object-cover border border-border"
                  />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-bold text-muted-foreground">
                    {(group.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{group.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" /> {formatMembers(group.members)}
                    </span>
                    {group.privacy !== 'unknown' && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        {group.privacy === 'public' ? (
                          <><Globe className="h-3 w-3" /> {t('Publico')}</>
                        ) : (
                          <><Lock className="h-3 w-3" /> {t('Privado')}</>
                        )}
                      </span>
                    )}
                    {group.postsPerDay > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {group.postsPerDay} posts/{t('dia')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats badges */}
                <div className="flex gap-1.5 shrink-0">
                  {group.members > 0 && (
                    <Badge variant="outline" className="text-[10px]">
                      {fmtNum.format(group.members)}
                    </Badge>
                  )}
                  <Badge
                    variant={group.privacy === 'public' ? 'default' : 'secondary'}
                    className="text-[10px]"
                  >
                    {group.privacy === 'public'
                      ? t('Publico')
                      : group.privacy === 'private'
                        ? t('Privado')
                        : '?'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isSearching && results.length === 0 && (
        <div className="flex flex-col items-center py-16 text-center">
          <Search className="mb-3 h-10 w-10 text-muted-foreground/20" />
          <h3 className="text-sm font-medium mb-1">{t('Extrator de Grupos')}</h3>
          <p className="text-xs text-muted-foreground max-w-sm">
            {t('Busque grupos do Facebook por palavra-chave. Os resultados aparecem em tempo real. Selecione e importe direto pra sua colecao.')}
          </p>
        </div>
      )}
    </div>
  )
}
